import React, { useState, useEffect, useRef } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { useOnboardingFlow } from '../contexts/OnboardingFlowContext';

/**
 * PWA Install Prompt
 * Shows a banner encouraging mobile users to install the app to their home screen.
 * Only shows on mobile, only shows once per session unless dismissed.
 */
const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const deferredPromptRef = useRef(null);
  const { isActive: walkthroughActive } = useOnboardingFlow();

  // True when running as the installed PWA (standalone) on any platform.
  const isStandalone = () =>
    (typeof window !== 'undefined') && (
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      (typeof document !== 'undefined' && document.referrer.startsWith('android-app://'))
    );

  useEffect(() => {
    // Never show again once installed (persisted per device/browser).
    if (localStorage.getItem('pwa_installed') === 'true') return;
    // If already launched as the installed app, record it and never prompt.
    if (isStandalone()) { localStorage.setItem('pwa_installed', 'true'); return; }
    // Dismissed for this browser session.
    if (sessionStorage.getItem('pwa_dismissed')) return;

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Only show on mobile-sized viewports (matches router/layout breakpoint = 1024px)
    if (window.innerWidth >= 1024) return;

    // For Android/Chrome — listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setTimeout(() => setShowPrompt(true), 3000); // Show after 3s
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Once the app is installed, hide the prompt for good.
    const installedHandler = () => {
      localStorage.setItem('pwa_installed', 'true');
      deferredPromptRef.current = null;
      setShowPrompt(false);
    };
    window.addEventListener('appinstalled', installedHandler);

    // For iOS — show instructions after delay
    if (iOS) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPromptRef.current) {
      deferredPromptRef.current.prompt();
      const result = await deferredPromptRef.current.userChoice;
      if (result.outcome === 'accepted') {
        localStorage.setItem('pwa_installed', 'true');
        setShowPrompt(false);
      }
      deferredPromptRef.current = null;
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    sessionStorage.setItem('pwa_dismissed', 'true');
  };

  if (!showPrompt) return null;
  // Belt-and-suspenders: if we somehow got here while installed/standalone, hide.
  if (isStandalone() || localStorage.getItem('pwa_installed') === 'true') return null;
  // Walkthrough priority — never block the guided walkthrough with the
  // install banner. Locked 2026-06-04 — Walkthrough Fix Pass #1/#2.
  if (walkthroughActive) return null;

  return (
    <div
      className="fixed left-0 right-0 z-[9990] p-3 lg:hidden pointer-events-none"
      style={{
        // Sit ABOVE the mobile bottom nav (≈64px) + safe-area inset so the
        // prompt never covers Home/Back/navbar. Walkthrough Fix Pass #2/#9.
        bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
        animation: 'slideUp 0.3s ease-out',
      }}
      data-testid="pwa-install-prompt"
    >
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 pointer-events-auto">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(145deg, #4B8DD6, #3470B8)' }}>
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800">Add RemodDash to Home Screen</p>
            {isIOS ? (
              <p className="text-xs text-gray-500 mt-0.5">
                Tap <span className="inline-block px-1 py-0.5 bg-gray-100 rounded text-[10px] font-mono">Share</span> then <span className="inline-block px-1 py-0.5 bg-gray-100 rounded text-[10px] font-mono">Add to Home Screen</span>
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-0.5">Get quick access from your home screen — just like a native app.</p>
            )}
            {!isIOS && deferredPromptRef.current && (
              <button
                onClick={handleInstall}
                className="mt-2.5 flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold text-white transition-colors"
                style={{ background: 'linear-gradient(145deg, #4B8DD6, #3470B8)' }}
                data-testid="pwa-install-btn"
              >
                <Download className="w-3.5 h-3.5" /> Install
              </button>
            )}
          </div>
          <button onClick={handleDismiss} className="p-1 text-gray-300 hover:text-gray-500 transition-colors" data-testid="pwa-dismiss-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
